import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  FaShoppingBasket, FaBars, FaTimes, FaSearch, FaUser,
  FaSignOutAlt, FaSignInAlt, FaChevronDown
} from 'react-icons/fa';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { API_BASE_URL } from '../config';
import { CATEGORIES } from '../constants';
import '../styles/Header.css';

export { CATEGORIES } from '../constants';

const MENU = [
  { name: 'الرئيسية', path: '/' },
  { name: 'المقالات', path: '/articles' },
  { name: 'المنتجات', path: '/products' },
  { name: 'التحميلات', path: '/links' }
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [catsOpen, setCatsOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [query, setQuery] = useState('');

  const { getCartCount } = useCart();
  const { user, isAuthenticated, logout } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const catsRef = useRef(null);
  const userRef = useRef(null);
  const count = getCartCount();

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* close everything when the route changes */
  useEffect(() => {
    setMenuOpen(false);
    setCatsOpen(false);
    setUserOpen(false);
  }, [location.pathname, location.search]);

  /* lock the page behind the mobile drawer */
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  /* escape closes any open layer */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      setMenuOpen(false);
      setCatsOpen(false);
      setUserOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /* click outside the dropdowns */
  useEffect(() => {
    const onClick = (e) => {
      if (catsRef.current && !catsRef.current.contains(e.target)) setCatsOpen(false);
      if (userRef.current && !userRef.current.contains(e.target)) setUserOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    navigate('/articles?q=' + encodeURIComponent(q));
    setQuery('');
    setMenuOpen(false);
  };

  const onLogout = () => {
    logout();
    setUserOpen(false);
    navigate('/');
  };

  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <header className={'header' + (isScrolled ? ' scrolled' : '')}>
      <div className="header-container">
        <Link to="/" className="logo" aria-label="الصفحة الرئيسية">
          {theme.logoImage ? (
            <img src={API_BASE_URL + theme.logoImage} alt={theme.logoText || 'Maman Algérienne'} />
          ) : (
            <span className="logo-lockup">
              <span className="logo-mark" aria-hidden="true" />
              <span className="logo-words">
                <span className="logo-name">{theme.logoText || 'Maman Algérienne'}</span>
                <span className="logo-sub">مجتمع الأمهات</span>
              </span>
            </span>
          )}
        </Link>

        {/* ---------- desktop navigation ---------- */}
        <nav className="nav-desktop" aria-label="التنقل الرئيسي">
          <ul>
            {MENU.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={'nav-link' + (isActive(item.path) ? ' active' : '')}
                  aria-current={isActive(item.path) ? 'page' : undefined}
                >
                  {item.name}
                </Link>
              </li>
            ))}
            <li className="nav-dropdown" ref={catsRef}>
              <button
                type="button"
                className={'nav-link nav-dropdown-btn' + (catsOpen ? ' open' : '')}
                onClick={() => setCatsOpen((v) => !v)}
                aria-expanded={catsOpen}
                aria-haspopup="true"
              >
                الأقسام
                <FaChevronDown aria-hidden="true" className="chevron" />
              </button>
              <AnimatePresence>
                {catsOpen && (
                  <motion.ul
                    className="dropdown-panel"
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.18 }}
                  >
                    {CATEGORIES.map((c) => (
                      <li key={c.key}>
                        <Link to={'/articles?category=' + c.key} className="dropdown-item">
                          <span className="dropdown-icon" aria-hidden="true">{c.icon}</span>
                          {c.name}
                        </Link>
                      </li>
                    ))}
                  </motion.ul>
                )}
              </AnimatePresence>
            </li>
          </ul>
        </nav>

        {/* ---------- actions ---------- */}
        <div className="header-actions">
          <form onSubmit={onSearch} className="search-form" role="search">
            <label htmlFor="site-search" className="sr-only">ابحث في المقالات</label>
            <FaSearch className="search-glyph" aria-hidden="true" />
            <input
              id="site-search"
              type="search"
              placeholder="ابحثي عن مقال..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button type="submit" className="search-go">بحث</button>
          </form>

          <Link to="/cart" className="cart-icon" aria-label={'سلة التسوق، ' + count + ' منتج'}>
            <FaShoppingBasket aria-hidden="true" />
            {count > 0 && <span className="cart-badge">{count}</span>}
          </Link>

          {isAuthenticated ? (
            <div className="user-menu" ref={userRef}>
              <button
                type="button"
                className="user-btn"
                onClick={() => setUserOpen((v) => !v)}
                aria-expanded={userOpen}
                aria-haspopup="true"
              >
                <span className="user-initial" aria-hidden="true">
                  {(user && user.fullName ? user.fullName.trim().charAt(0) : '؟')}
                </span>
                <span className="user-name">{user && user.fullName ? user.fullName.split(' ')[0] : 'حسابي'}</span>
              </button>
              <AnimatePresence>
                {userOpen && (
                  <motion.div
                    className="user-dropdown"
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.18 }}
                  >
                    <p className="user-email">{user && user.phoneNumber}</p>
                    {user && user.isAdmin && <Link to="/admin">لوحة التحكم</Link>}
                    <button type="button" onClick={onLogout}>
                      <FaSignOutAlt aria-hidden="true" /> تسجيل الخروج
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <Link to="/login" className="login-btn">
              <FaSignInAlt aria-hidden="true" />
              <span>دخول</span>
            </Link>
          )}

          <button
            type="button"
            className="mobile-menu-toggle"
            onClick={() => setMenuOpen((v) => !v)}
            aria-expanded={menuOpen}
            aria-controls="mobile-drawer"
            aria-label={menuOpen ? 'إغلاق القائمة' : 'فتح القائمة'}
          >
            {menuOpen ? <FaTimes aria-hidden="true" /> : <FaBars aria-hidden="true" />}
          </button>
        </div>
      </div>

      {/* ---------- mobile drawer ---------- */}
      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div
              className="drawer-veil"
              onClick={() => setMenuOpen(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />
            <motion.nav
              id="mobile-drawer"
              className="drawer"
              aria-label="قائمة الجوال"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.28, ease: [0.22, 0.68, 0.36, 1] }}
            >
              <form onSubmit={onSearch} className="drawer-search" role="search">
                <label htmlFor="drawer-search" className="sr-only">ابحث في المقالات</label>
                <input
                  id="drawer-search"
                  type="search"
                  placeholder="ابحثي عن مقال..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                <button type="submit" aria-label="بحث"><FaSearch aria-hidden="true" /></button>
              </form>

              <ul className="drawer-links">
                {MENU.map((item) => (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className={isActive(item.path) ? 'active' : ''}
                      aria-current={isActive(item.path) ? 'page' : undefined}
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>

              <p className="drawer-heading">الأقسام</p>
              <ul className="drawer-cats">
                {CATEGORIES.map((c) => (
                  <li key={c.key}>
                    <Link to={'/articles?category=' + c.key}>
                      <span aria-hidden="true">{c.icon}</span> {c.name}
                    </Link>
                  </li>
                ))}
              </ul>

              {isAuthenticated ? (
                <div className="drawer-foot">
                  {user && user.isAdmin && <Link className="drawer-btn ghost" to="/admin">لوحة التحكم</Link>}
                  <button type="button" className="drawer-btn ghost" onClick={onLogout}>
                    <FaSignOutAlt aria-hidden="true" /> تسجيل الخروج
                  </button>
                </div>
              ) : (
                <div className="drawer-foot">
                  <Link className="drawer-btn" to="/login"><FaUser aria-hidden="true" /> تسجيل الدخول</Link>
                  <Link className="drawer-btn ghost" to="/register">حساب جديد</Link>
                </div>
              )}
            </motion.nav>
          </>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
